/**
 * Google Maps API Script Loader
 * Dynamically loads the Google Maps JavaScript API with Places library
 */

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google?: {
      maps?: unknown
    }
  }
}

let isLoading = false
let isLoaded = false
const callbacks: Array<(error?: Error) => void> = []

/**
 * Load Google Maps JavaScript API
 * @returns Promise that resolves when the API is loaded
 */
export const loadGoogleMapsAPI = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (isLoaded) {
      resolve()
      return
    }

    // Add to callbacks queue
    callbacks.push(error => {
      if (error) {
        reject(error)
      } else {
        resolve()
      }
    })

    // If already loading, just wait for callbacks
    if (isLoading) {
      return
    }

    // Start loading
    isLoading = true

    // Check if API key exists
    const apiKey = import.meta.env.VITE_MAP_KEYS
    if (!apiKey) {
      const error = new Error("Google Maps API key is not configured")
      console.error(error)
      callbacks.forEach(cb => {
        cb(error)
      })
      callbacks.length = 0
      isLoading = false
      return
    }

    // Create script element
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    script.async = true
    script.defer = true

    script.onload = () => {
      isLoaded = true
      isLoading = false
      console.log("✅ Google Maps API loaded successfully")

      // Resolve all pending callbacks
      callbacks.forEach(cb => {
        cb()
      })
      callbacks.length = 0
    }

    script.onerror = () => {
      isLoading = false
      const error = new Error("Failed to load Google Maps API")
      console.error(error)

      // Reject all pending callbacks
      callbacks.forEach(cb => {
        cb(error)
      })
      callbacks.length = 0
    }

    // Append script to document
    document.head.appendChild(script)
  })
}

/**
 * Check if Google Maps API is loaded
 */
export const isGoogleMapsLoaded = (): boolean => {
  return typeof window !== "undefined" && typeof window.google?.maps !== "undefined"
}
